import { useQuery } from '@tanstack/react-query';
import { useIdentity } from '@internal/shared';
import {
  DIAM_API_PATH,
  DIAM_PAGE_LIMIT,
  DIAM_TEAM_ID,
  diamQueryKeys,
  getIdentitiesPage,
} from './diamApi';
import { useApiEssentials } from '../../shared/hooks/useApiEssentials';

const CURRENT_IDENTITY_FILTER = {
  teamId: DIAM_TEAM_ID,
  teamScope: 'SINGLE' as const,
};

export function useCurrentIdentity() {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);
  const { profile } = useIdentity();
  const userId = profile?.userId;

  return useQuery({
    queryKey: [
      ...diamQueryKeys.identities(CURRENT_IDENTITY_FILTER),
      'current',
      userId ?? '',
    ],
    queryFn: async () => {
      if (!userId) {
        throw new Error('A user id is required');
      }

      const idToken = await getIdToken();
      const page = await getIdentitiesPage(apiUrl, idToken, {
        page: 0,
        limit: DIAM_PAGE_LIMIT,
        filter: CURRENT_IDENTITY_FILTER,
      });

      const match = (page.elements ?? []).find(
        identity => identity.externalId === userId || identity.id === userId,
      );

      if (!match) {
        throw new Error('Identity not found for the signed-in user');
      }

      return match;
    },
    enabled: isSignedIn && Boolean(userId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}
